import { getBaseUrl } from '@databases/database-state';
import { DatabaseTableSkeletonRefresh } from '@databases/frontend/DatabaseTableSkeletonRefresh';
import { getPinnedLeadingFieldCountFromProperties } from '@databases/pinned-columns/pinned-leading-view-properties';
import { appStore } from '@databases/redux/store';
import {
  type TableV2Instance,
  type TableV2Reads
} from '@databases/view-layout-table-v2/data';
import useView from '@databases/views/useView';
import React, { lazy, useLayoutEffect, useSyncExternalStore } from 'react';
import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getExperimentValue } from '@canvas/client-feature-flags/experiments';
import { dynamicImport } from '@canvas/dynamic-import';
import { type TableV2RenderSnapshot } from '../table-v2-host.tsx';
import { createAttachmentFrontendAdapter } from './create-attachment-resource.ts';
import { createBacklinkReadSource } from './create-backlink-read-source.ts';
import { createEntryDetailsReadSource } from './create-entry-details-read-source.ts';
import { createRelatedReadSourceContext } from './related-read-source-context.ts';

// Keep the former frontend data API available during the package migration.
// New consumers import these contracts from the owning Table V2 package.
// fallow-ignore-next-line unused-export -- Compatibility forwarding export.
export { createTableV2Instance } from '@databases/view-layout-table-v2/data';
export {
  // fallow-ignore-next-line unused-type -- Compatibility forwarding export.
  type TableV2DataSource,
  // fallow-ignore-next-line unused-type -- Compatibility forwarding export.
  type TableV2Projection
} from '@databases/view-layout-table-v2/data';

const TableV2Layout = lazy(() => dynamicImport(() => import('./TableV2Layout.tsx')));

/**
 * One mounted binding follows structural snapshots without replacing the table.
 * Publish presence placement in the parent layout effect, after the child's
 * committed geometry. Source acquisition and disposal remain bootstrap-owned;
 * React only subscribes and acknowledges the snapshot it rendered.
 */
export function createTableV2RenderBinding(
  source: {
    getSnapshot(): Omit<TableV2Instance, 'dispose'> | undefined;
    subscribe(listener: VoidFunction): VoidFunction;
    commit(table: Omit<TableV2Instance, 'dispose'> | undefined): void;
    readonly reads: TableV2Reads;
  },
  contentId?: string,
  resources?: {
    readonly getSnapshot: () => AbortSignal;
    readonly subscribe: (listener: VoidFunction) => VoidFunction;
  }
): TableV2RenderSnapshot {
  const attachmentAdapter = contentId
    ? createAttachmentFrontendAdapter({
        contentId,
        store: appStore,
        hideMigrationPlaceholders: isFeatureFlagEnabled('uiBrieImprovements'),
        enforceDataSecurityPolicy:
          currentHostFrameContext.data.dataSecurityPolicy?.attachmentDownload !== true
      })
    : undefined;

  const relatedSourceContext = contentId
    ? createRelatedReadSourceContext(contentId)
    : undefined;
  const entryDetailsSource = relatedSourceContext
    ? createEntryDetailsReadSource({
        store: appStore,
        sourceContext: relatedSourceContext
      })
    : undefined;
  const backlinkSource = contentId
    ? createBacklinkReadSource({
        store: appStore,
        baseUrl: getBaseUrl(),
        contentId,
        getSourceContentId: relatedSourceContext?.getContentId
      })
    : undefined;

  return {
    status: 'ready',
    Component: function TableV2RenderBinding({ scrollElement }) {
      const { view } = useView();
      const resourceSignal = useSyncExternalStore(
        resources?.subscribe ?? subscribeNoResources,
        resources?.getSnapshot ?? getNoResourceSignal
      );
      const table = useSyncExternalStore(
        source.subscribe,
        source.getSnapshot,
        source.getSnapshot
      );
      useLayoutEffect(() => source.commit(table), [table]);

      if (!table) {
        return <DatabaseTableSkeletonRefresh />;
      }

      return (
        <TableV2Layout
          attachmentAdapter={attachmentAdapter}
          backlinkSource={backlinkSource}
          entryDetailsSource={entryDetailsSource}
          resourceSignal={resourceSignal}
          frozenLeadingColumnCount={
            getExperimentValue('databasesPinnedLeadingColumns', 'enabled', {
              fireExperimentExposure: false
            })
              ? (getPinnedLeadingFieldCountFromProperties(view.settings.properties) ?? 0)
              : 0
          }
          projection={table.projection}
          reads={source.reads}
          showNumberedEntries={view.settings.layout.showNumberedEntries}
          scrollElement={scrollElement}
        />
      );
    }
  };
}

const subscribeNoResources = () => () => {};
const getNoResourceSignal = () => undefined;
